/**
 * SONAUTO — QR dining-session tokens.
 *
 * This is the capability a printed QR code carries. It is the ONLY credential
 * the public customer-ordering API accepts, so its threat model drives every
 * design decision in this file.
 *
 * ---------------------------------------------------------------------------
 * THREAT MODEL
 * ---------------------------------------------------------------------------
 * 1. IDOR / ENUMERATION.
 *    A naive implementation encodes the table in the QR: `/order?table=15`.
 *    A guest then edits the URL to `table=16` and orders onto a stranger's
 *    bill, or walks the whole range and maps the restaurant. Every id in this
 *    payload is therefore an OPAQUE ULID (26 chars, 80 bits of randomness in
 *    the suffix) and NEVER a sequential integer, a table number or a table
 *    code. `tables.code` — the internal "T15" — must never enter a token.
 *    Even with an opaque id, guessing is pointless: the payload is bound by an
 *    HMAC, so mutating any field invalidates the token.
 *
 * 2. FORGERY / MUTATION.
 *    The token is `sq1.<payload>.<signature>` where the signature is
 *    HMAC-SHA256 over `sq1.<payload>` using a server-only secret
 *    (`env.QR_TOKEN_SECRET`). Changing the session id, the branch, the tenant
 *    or the expiry breaks the MAC. The MAC is checked BEFORE any database
 *    round-trip, so garbage never reaches D1.
 *
 * 3. TOKEN THEFT FROM THE DATABASE.
 *    The raw token is returned exactly once, to the slip renderer. Only its
 *    SHA-256 lands in `session_qr_tokens.token_hash`. A dump of D1 therefore
 *    yields no usable tokens. (`token_prefix` is a support-lookup aid and is
 *    far too short to be a credential.)
 *
 * 4. REPLAY AFTER THE VISIT ENDS.
 *    The token carries an expiry, but expiry alone is not enough: the API also
 *    re-checks `session_qr_tokens.status = 'ACTIVE'` and that the dining
 *    session is still open. A signed-but-revoked token is worthless. Never
 *    treat a VALID result from this module as authorisation on its own.
 *
 * 5. SHARED QR CODES.
 *    A photographed QR travels. The device cap (`dining_sessions.max_devices`)
 *    and `session_qr_tokens.max_scans` are the mitigations; the slip printed
 *    for the guest carries an explicit warning (see `qrSlipPayload`).
 *
 * Runtime: WebCrypto only — identical behaviour in Workers, browsers, Node 20+.
 */

import { randomToken, sha256Hex, shortCode, timingSafeEqual } from '@sonauto/utilities';

const enc = new TextEncoder();
const dec = new TextDecoder();

/**
 * Scheme marker. Bump this (and only this) if the payload shape ever changes;
 * old tokens then fail as MALFORMED instead of being misread.
 */
export const SESSION_TOKEN_SCHEME = 'sq1';

/** Hard ceiling on an accepted token. A QR cannot legitimately be longer. */
export const MAX_SESSION_TOKEN_LENGTH = 1024;

/** Nonce size. 256 bits of entropy — the token is unguessable on its own. */
export const SESSION_TOKEN_NONCE_BYTES = 32;

/**
 * How many leading characters of the raw token are kept for support lookup.
 *
 * This MUST be long enough to reach into the random nonce. The token starts
 * `sq1.` (4 chars) followed by base64url of a payload whose first six bytes are
 * always the literal `{"n":"` — which is exactly 8 base64 characters. A prefix
 * of 12 therefore covered nothing but that fixed header and was IDENTICAL for
 * every token ever issued, making `ix_session_qr_tokens_prefix` a useless index
 * and support lookup impossible. 20 spans 6 characters of the nonce (~6.8e10
 * combinations) while staying far too short to be a credential.
 */
export const TOKEN_PREFIX_LENGTH = 20;

// ---------------------------------------------------------------------------
// base64url
// ---------------------------------------------------------------------------

function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string): Uint8Array | null {
  if (!/^[A-Za-z0-9_-]*$/.test(value)) return null;
  const pad = value.length % 4 === 0 ? '' : '='.repeat(4 - (value.length % 4));
  try {
    const bin = atob(value.replace(/-/g, '+').replace(/_/g, '/') + pad);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

async function hmac(secret: string, data: string): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(data)));
}

// ---------------------------------------------------------------------------
// Payload
// ---------------------------------------------------------------------------

/**
 * The wire payload. Keys are single letters purely to keep the QR small — a
 * denser QR is a QR that still scans from a phone held at arm's length on a
 * badly lit table.
 *
 * `n` is FIRST on purpose: it makes the leading characters of the base64
 * payload vary between tokens, which is what makes `token_prefix` a useful
 * support-lookup discriminator instead of a constant.
 *
 * Everything here is opaque. There is deliberately NO table number, NO table
 * code, NO order id, NO price and NO guest data.
 */
interface WirePayload {
  /** Random nonce, base64url. Makes every token unique even for one session. */
  readonly n: string;
  /** Payload schema version. */
  readonly v: number;
  /** Tenant ULID. */
  readonly t: string;
  /** Branch ULID. */
  readonly b: string;
  /** Dining session ULID. */
  readonly s: string;
  /** Table ULID, or null for takeaway / delivery sessions. */
  readonly tb: string | null;
  /** Issued at, epoch SECONDS (server clock). */
  readonly i: number;
  /** Expires at, epoch SECONDS (server clock). */
  readonly e: number;
}

/** The ids a verified token binds. Nothing else may be trusted from a guest. */
export interface SessionTokenClaims {
  readonly tenantId: string;
  readonly branchId: string;
  readonly diningSessionId: string;
  readonly tableId: string | null;
  /** Epoch milliseconds. */
  readonly issuedAt: number;
  /** Epoch milliseconds. */
  readonly expiresAt: number;
  readonly version: number;
}

export interface IssueSessionTokenInput {
  readonly tenantId: string;
  readonly branchId: string;
  /** `null` for TAKEAWAY / DELIVERY sessions with no physical table. */
  readonly tableId: string | null;
  readonly diningSessionId: string;
  /** Epoch milliseconds, server clock. */
  readonly expiresAt: number;
  /** `env.QR_TOKEN_SECRET`. Never a per-tenant value the client can influence. */
  readonly secret: string;
  /** Epoch milliseconds, server clock. */
  readonly now: number;
  /**
   * Reuse the dining session's existing human code instead of minting a new
   * one. Pass `dining_sessions.session_code` so the slip and the session agree.
   */
  readonly shortCode?: string;
}

export interface IssuedSessionToken {
  /**
   * The RAW token. Returned exactly once, handed to the QR/slip renderer and
   * then dropped. It must never be logged, never stored and never echoed back
   * in an API response.
   */
  readonly token: string;
  /** SHA-256 hex — the ONLY form that is written to `session_qr_tokens`. */
  readonly tokenHash: string;
  /** `session_qr_tokens.token_prefix`. Support lookup / slip display only. */
  readonly tokenPrefix: string;
  /** Human-readable code for the slip ("mã bàn"), e.g. `7K3M9P`. */
  readonly shortCode: string;
  readonly issuedAt: number;
  readonly expiresAt: number;
}

/**
 * Mint a signed QR session token.
 *
 * The caller is responsible for persisting `tokenHash` (never `token`) into
 * `session_qr_tokens` together with the same tenant/branch/session ids, and for
 * setting `expires_at` to the same instant that is signed here.
 */
export async function issueSessionToken(input: IssueSessionTokenInput): Promise<IssuedSessionToken> {
  if (input.secret.length < 32) {
    throw new Error('issueSessionToken: QR_TOKEN_SECRET must be at least 32 characters.');
  }
  if (!Number.isFinite(input.now) || !Number.isFinite(input.expiresAt)) {
    throw new Error('issueSessionToken: now/expiresAt must be finite epoch milliseconds.');
  }
  if (input.expiresAt <= input.now) {
    throw new Error('issueSessionToken: expiresAt must be in the future.');
  }

  const payload: WirePayload = {
    n: randomToken(SESSION_TOKEN_NONCE_BYTES),
    v: 1,
    t: input.tenantId,
    b: input.branchId,
    s: input.diningSessionId,
    tb: input.tableId,
    i: Math.floor(input.now / 1000),
    e: Math.floor(input.expiresAt / 1000),
  };

  const body = toBase64Url(enc.encode(JSON.stringify(payload)));
  const signingInput = `${SESSION_TOKEN_SCHEME}.${body}`;
  const signature = toBase64Url(await hmac(input.secret, signingInput));
  const token = `${signingInput}.${signature}`;

  return {
    token,
    tokenHash: await sha256Hex(token),
    tokenPrefix: token.slice(0, TOKEN_PREFIX_LENGTH),
    shortCode: input.shortCode ?? shortCode(6),
    issuedAt: payload.i * 1000,
    expiresAt: payload.e * 1000,
  };
}

// ---------------------------------------------------------------------------
// Verification
// ---------------------------------------------------------------------------

export type SessionTokenFailure = 'MALFORMED' | 'BAD_SIGNATURE' | 'EXPIRED';

export type SessionTokenVerification =
  | {
      readonly status: 'VALID';
      readonly claims: SessionTokenClaims;
      /** Lookup key for `session_qr_tokens.token_hash`. */
      readonly tokenHash: string;
      readonly tokenPrefix: string;
    }
  | { readonly status: SessionTokenFailure };

function isUlidLike(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9A-HJKMNP-TV-Z]{26}$/.test(value);
}

function parsePayload(raw: unknown): WirePayload | null {
  if (raw === null || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  if (p.v !== 1) return null;
  if (typeof p.n !== 'string' || p.n.length < 16 || p.n.length > 128) return null;
  if (!isUlidLike(p.t) || !isUlidLike(p.b) || !isUlidLike(p.s)) return null;
  if (p.tb !== null && !isUlidLike(p.tb)) return null;
  if (typeof p.i !== 'number' || !Number.isFinite(p.i)) return null;
  if (typeof p.e !== 'number' || !Number.isFinite(p.e)) return null;
  return {
    n: p.n,
    v: 1,
    t: p.t,
    b: p.b,
    s: p.s,
    tb: p.tb as string | null,
    i: p.i,
    e: p.e,
  };
}

/**
 * Verify a token's structure, signature and expiry.
 *
 * IMPORTANT: a `VALID` result proves only that the token was minted by this
 * server and has not expired. It is NOT authorisation. The caller must still
 * look the token hash up in `session_qr_tokens`, confirm `status = 'ACTIVE'`,
 * and confirm the dining session is still open — that is what makes staff
 * revocation take effect immediately.
 *
 * The signature comparison is constant-time (`timingSafeEqual` over the raw MAC
 * bytes), so an attacker cannot recover a valid MAC byte-by-byte from response
 * timing. The signature is checked BEFORE the payload is parsed, so malformed
 * JSON from an unsigned token can never reach `JSON.parse` — and MALFORMED vs
 * BAD_SIGNATURE never distinguishes a forged signature from a corrupt payload
 * in a way that helps an attacker, because both are reported to the guest as
 * one indistinguishable error code by the API layer.
 */
export async function verifySessionToken(
  token: string | null | undefined,
  secret: string,
  now: number,
): Promise<SessionTokenVerification> {
  if (typeof token !== 'string' || token.length === 0 || token.length > MAX_SESSION_TOKEN_LENGTH) {
    return { status: 'MALFORMED' };
  }
  const parts = token.split('.');
  if (parts.length !== 3) return { status: 'MALFORMED' };
  const [scheme, body, signature] = parts as [string, string, string];
  if (scheme !== SESSION_TOKEN_SCHEME || body === '' || signature === '') {
    return { status: 'MALFORMED' };
  }

  const provided = fromBase64Url(signature);
  if (provided === null) return { status: 'MALFORMED' };

  const expected = await hmac(secret, `${scheme}.${body}`);
  if (!timingSafeEqual(provided, expected)) return { status: 'BAD_SIGNATURE' };

  const bodyBytes = fromBase64Url(body);
  if (bodyBytes === null) return { status: 'MALFORMED' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(dec.decode(bodyBytes)) as unknown;
  } catch {
    return { status: 'MALFORMED' };
  }

  const payload = parsePayload(parsed);
  if (payload === null) return { status: 'MALFORMED' };

  // Expiry is checked against the SERVER clock only; the client's clock is
  // never an input to this decision.
  if (now >= payload.e * 1000) return { status: 'EXPIRED' };

  return {
    status: 'VALID',
    claims: {
      tenantId: payload.t,
      branchId: payload.b,
      diningSessionId: payload.s,
      tableId: payload.tb,
      issuedAt: payload.i * 1000,
      expiresAt: payload.e * 1000,
      version: payload.v,
    },
    tokenHash: await sha256Hex(token),
    tokenPrefix: token.slice(0, TOKEN_PREFIX_LENGTH),
  };
}

// ---------------------------------------------------------------------------
// URLs
// ---------------------------------------------------------------------------

/** Query parameter the customer PWA reads the token from. */
export const QR_TOKEN_QUERY_PARAM = 't';

/**
 * Build the URL encoded into the QR image.
 *
 * The token travels in the query string rather than the path so that the
 * customer PWA can be served as a single static route. It is a bearer
 * credential: `Referrer-Policy: strict-origin-when-cross-origin` (set by the
 * API's security headers) keeps it out of third-party referrers, and the PWA is
 * expected to strip it from `location` after exchanging it for a stored session.
 */
export function buildQrUrl(baseUrl: string, token: string): string {
  const base = baseUrl.replace(/\/+$/, '');
  return `${base}/q?${QR_TOKEN_QUERY_PARAM}=${encodeURIComponent(token)}`;
}

/**
 * Extract the token from a scanned URL. Returns `null` for anything that is
 * not a well-formed URL carrying a plausible token — shape only; authenticity
 * is `verifySessionToken`'s job.
 */
export function parseQrUrl(url: string): string | null {
  if (typeof url !== 'string' || url.length === 0 || url.length > 4096) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const token = parsed.searchParams.get(QR_TOKEN_QUERY_PARAM);
  if (token === null || token.length === 0 || token.length > MAX_SESSION_TOKEN_LENGTH) return null;
  return token.startsWith(`${SESSION_TOKEN_SCHEME}.`) ? token : null;
}

// ---------------------------------------------------------------------------
// Printed slip
// ---------------------------------------------------------------------------

export interface QrSlipInput {
  readonly restaurantName: string;
  readonly branchName: string;
  /** `tables.display_name`, e.g. "Bàn 15". NEVER `tables.code`. */
  readonly tableDisplayName: string | null;
  /** `dining_sessions.session_code`. */
  readonly sessionCode: string;
  readonly token: string;
  /** Public origin of the customer PWA, e.g. `https://order.sonauto.vn`. */
  readonly baseUrl: string;
  /** Epoch milliseconds. */
  readonly issuedAt: number;
  /** Epoch milliseconds. */
  readonly expiresAt: number;
  readonly guestCount?: number;
}

/**
 * Everything a 58/80mm thermal slip needs, already in Vietnamese.
 *
 * Staff-facing and guest-facing print stays Vietnamese by product rule: the
 * multilingual surface starts once the guest has scanned and the app has
 * negotiated a locale. Keeping the slip monolingual also keeps it short enough
 * to print on one tear.
 */
export interface QrSlipPayload {
  readonly restaurantName: string;
  readonly branchName: string;
  readonly tableDisplayName: string;
  readonly sessionCode: string;
  /** The URL to encode into the QR image. */
  readonly qrUrl: string;
  /** `dd/MM/yyyy HH:mm`, Asia/Ho_Chi_Minh, formatted by the caller if needed. */
  readonly issuedAt: number;
  readonly expiresAt: number;
  readonly validMinutes: number;
  readonly guestCount: number | null;
  /** Scan instructions, Vietnamese, in print order. */
  readonly instructionsVi: readonly string[];
  /** Do-not-share warning, Vietnamese. Printed emphasised. */
  readonly warningVi: string;
}

export function qrSlipPayload(input: QrSlipInput): QrSlipPayload {
  const validMinutes = Math.max(0, Math.round((input.expiresAt - input.issuedAt) / 60000));
  return {
    restaurantName: input.restaurantName,
    branchName: input.branchName,
    tableDisplayName: input.tableDisplayName ?? 'Mang về',
    sessionCode: input.sessionCode,
    qrUrl: buildQrUrl(input.baseUrl, input.token),
    issuedAt: input.issuedAt,
    expiresAt: input.expiresAt,
    validMinutes,
    guestCount: input.guestCount ?? null,
    instructionsVi: [
      'Mở camera điện thoại và quét mã QR ở trên.',
      'Chọn ngôn ngữ của bạn ở góc trên màn hình.',
      'Chọn món, thêm ghi chú rồi bấm "Gửi đơn".',
      'Nhân viên sẽ xác nhận đơn của bạn trong giây lát.',
      `Mã bàn: ${input.sessionCode} — đọc mã này cho nhân viên nếu cần hỗ trợ.`,
      `Mã có hiệu lực trong ${validMinutes} phút.`,
    ],
    warningVi:
      'KHÔNG chia sẻ mã QR này cho người ngoài bàn. Mọi món gọi bằng mã này đều được tính vào hoá đơn của bàn bạn.',
  };
}

// ---------------------------------------------------------------------------
// Device fingerprint
// ---------------------------------------------------------------------------

export interface DeviceFingerprintParts {
  /** `User-Agent`. Truncated before hashing; the full string is never stored. */
  readonly userAgent?: string | null;
  /** `Accept-Language`. */
  readonly acceptLanguage?: string | null;
  /** `Sec-CH-UA-Platform` or equivalent. */
  readonly platform?: string | null;
  /** ALREADY-HASHED client IP (`Vars.ipHash`). Never a raw address. */
  readonly ipHash?: string | null;
  /** The dining session id, so a fingerprint cannot be correlated across visits. */
  readonly diningSessionId: string;
  /** Server-side pepper (`env.DEVICE_SECRET_PEPPER`). */
  readonly pepper: string;
}

/**
 * A stable, COARSE hash of a few request hints, used for exactly one thing:
 * counting how many phones are attached to one dining session so
 * `dining_sessions.max_devices` can be enforced.
 *
 * THIS IS NOT A TRACKING IDENTIFIER, and it is built so that it cannot become
 * one:
 *   - the dining session id is mixed in, so the same phone at the same table
 *     tomorrow produces a completely different value — there is no cross-visit
 *     or cross-branch identity to join on;
 *   - a server-side pepper is mixed in, so the value cannot be recomputed by
 *     anyone who obtains the database;
 *   - the inputs are deliberately coarse (User-Agent truncated to its family,
 *     language, platform, already-hashed IP). No canvas, no fonts, no screen
 *     metrics, no cookies, no device ids — nothing that would raise the entropy
 *     to the level where it identifies a person rather than a session slot;
 *   - collisions are ACCEPTABLE. Two similar phones colliding means the table
 *     gets one more shared slot, which is a mild UX quirk, not a security hole.
 *     Under-counting devices is safe; identifying guests is not.
 */
export async function deviceFingerprint(parts: DeviceFingerprintParts): Promise<string> {
  const ua = (parts.userAgent ?? '').slice(0, 180).toLowerCase();
  const lang = (parts.acceptLanguage ?? '').slice(0, 64).toLowerCase();
  const platform = (parts.platform ?? '').slice(0, 64).toLowerCase();
  const ip = parts.ipHash ?? '';
  return sha256Hex(`qrfp:v1|${parts.pepper}|${parts.diningSessionId}|${ua}|${lang}|${platform}|${ip}`);
}
